import type { Curve } from './curve'

/**
 * Der Referenzgang — Normkurven je Gelenk und Ebene.
 *
 * Diese Tabellen sind der Nullzustand des Systems: der vorgeschriebene Gang, an
 * dem sich der gerechnete Gang und jedes Gangbild messen. Sie stammen aus der
 * Vorlage und wurden maschinell übernommen, nicht abgeschrieben — bei 62
 * Kurven mit zusammen mehreren hundert Stützstellen ist Abtippen keine
 * vertretbare Fehlerquelle.
 *
 * Je Ebene liegen bis zu drei Sätze vor:
 *   `kf`  — Gehen, die Grundlage
 *   `car` — Tragen (gedämpfte Amplituden, Arme gebunden)
 *   `run` — Laufen
 *
 * `sd` ist die Streuung des Normbands in Grad; sie zeichnet im Diagramm den
 * grauen Korridor um die Kurve. `kinetic: false` heisst, dass für dieses Gelenk
 * keine Momente gerechnet werden — das Becken ist kein Antrieb, sondern die
 * Aufhängung.
 */

export interface JointPlane {
  /** Bezeichnung der Bewegung in dieser Ebene. */
  t: string
  /** Streuung des Normbands in Grad. */
  sd: number
  /** Gehen. */
  kf: Curve
  /** Tragen. */
  car?: Curve
  /** Laufen. */
  run?: Curve
  /** Sprinten. */
  spr?: Curve
}

export interface JointDef {
  label: string
  order: number
  /** Paarig vorhanden — links und rechts getrennt gerechnet. */
  sided?: boolean
  /** false: kein Antrieb, es werden keine Momente gerechnet. */
  kinetic?: boolean
  planes: Partial<Record<'sag' | 'fro' | 'tra', JointPlane>>
}

export const PLANES = {
  sag: { label: 'Sagittal', short: 'Sag' },
  fro: { label: 'Frontal', short: 'Fro' },
  tra: { label: 'Transversal', short: 'Tra' },
} as const

export type PlaneKey = keyof typeof PLANES

export const JOINTS: Record<string, JointDef> = {
  pelvis: {
    label: "Becken", order: 0, kinetic: false,
    planes: {
      sag: { t: "Kippung", sd: 2.5, kf: [[0,7],[12,5],[25,6],[38,8],[50,7],[62,5],[75,6],[88,8]], car: [[0,4],[12,2.5],[25,3.5],[38,5],[50,4],[62,2.5],[75,3.5],[88,5]], run: [[0,11],[15,8],[30,9],[45,12],[60,11],[75,8],[90,10]] },
      fro: { t: "Obliquität", sd: 2, kf: [[0,0],[10,4],[25,5],[40,3],[50,0],[60,-4],[75,-5],[90,-3]], car: [[0,0],[10,2.5],[25,3],[40,2],[50,0],[60,-2.5],[75,-3],[90,-2]], run: [[0,0],[10,7],[22,8],[34,4],[45,-2],[60,-8],[74,-9],[88,-4]] },
      tra: { t: "Rotation", sd: 2.5, kf: [[0,5],[15,2],[30,-1],[45,-4],[50,-5],[62,-3],[78,1],[90,4]], car: [[0,2],[15,0.8],[30,-0.4],[45,-1.6],[50,-2],[62,-1.2],[78,0.4],[90,1.6]], run: [[0,9],[15,3],[30,-3],[45,-8],[55,-9],[70,-3],[85,5]] }
    }
  },
  hip: {
    label: "Hüfte", order: 1, sided: true, kinetic: true,
    planes: {
      sag: { t: "Flexion / Extension", sd: 5,
        kf: [[0,30],[8,26],[16,21],[24,16],[32,11],[40,5],[48,-1],[55,-8],[60,-10],[66,-4],[72,6],[80,19],[88,27],[94,30]],
        car: [[0,35],[8,31],[16,27],[24,22],[32,17],[40,12],[48,6],[56,0],[63,-2],[70,8],[78,18],[86,28],[94,33]], run: [[0,32],[8,25],[16,17],[24,9],[31,0],[38,-11],[44,-6],[52,9],[60,24],[68,36],[76,42],[84,41],[92,37]],
        spr: [[0,58],[6,42],[12,26],[17,10],[22,-9],[28,-19],[34,-9],[42,18],[50,45],[58,66],[66,80],[74,84],[82,77],[91,70]] },
      fro: { t: "Adduktion / Abduktion", sd: 3,
        kf: [[0,-2],[6,3],[14,6],[25,7],[38,5],[50,2],[58,-1],[65,-5],[75,-6],[85,-4],[94,-3]],
        car: [[0,-1],[6,4],[14,7],[25,8],[38,6],[50,3],[58,0],[65,-4],[75,-5],[85,-3],[94,-2]], run: [[0,-3],[8,4],[16,7],[26,6],[36,2],[46,-1],[56,-3],[68,-4],[80,-4],[92,-4]] },
      tra: { t: "Innen- / Außenrotation", sd: 4,
        kf: [[0,-3],[12,2],[30,4],[50,3],[62,-2],[75,-6],[88,-5],[95,-4]], run: [[0,-5],[12,3],[28,6],[42,4],[54,-3],[68,-9],[82,-8],[93,-6]] }
    }
  },
  knee: {
    label: "Knie", order: 2, sided: true, kinetic: true,
    planes: {
      sag: { t: "Flexion / Extension", sd: 6,
        kf: [[0,4],[8,14],[15,18],[25,14],[35,9],[45,7],[52,12],[58,22],[64,40],[70,55],[74,60],[80,52],[86,36],[92,18],[97,7]],
        car: [[0,8],[8,18],[15,22],[25,18],[35,14],[45,12],[52,16],[58,25],[64,39],[70,49],[74,52],[80,45],[86,33],[92,19],[97,11]], run: [[0,19],[8,32],[15,39],[22,35],[28,28],[33,21],[38,16],[44,42],[50,70],[58,98],[66,106],[74,92],[82,62],[90,32],[96,23]],
        spr: [[0,31],[7,42],[13,39],[18,30],[22,24],[27,46],[33,84],[40,124],[47,148],[55,150],[63,128],[71,98],[79,68],[87,45],[94,34]] },
      fro: { t: "Varus / Valgus", sd: 3,
        kf: [[0,0],[15,4],[30,3],[50,1],[65,-2],[80,-4],[92,-1]], run: [[0,0],[14,6],[28,4],[44,1],[60,-3],[76,-5],[90,-2]] },
      tra: { t: "Innen- / Außenrotation", sd: 4,
        kf: [[0,-2],[15,3],[35,5],[55,2],[70,-6],[85,-8],[95,-5]], run: [[0,-3],[14,5],[32,7],[50,3],[66,-8],[82,-11],[93,-7]] }
    }
  },
  ankle: {
    label: "Sprunggelenk", order: 3, sided: true, kinetic: true,
    planes: {
      sag: { t: "Dorsal- / Plantarflexion", sd: 4,
        kf: [[0,0],[5,-4],[10,-5],[18,0],[28,5],[38,9],[45,11],[50,10],[55,5],[58,-2],[62,-14],[65,-17],[69,-8],[74,1],[80,5],[87,6],[93,3],[97,1]], run: [[0,-4],[3,7],[6,14],[10,21],[14,25],[18,24],[22,18],[26,3],[30,-22],[35,-16],[42,-7],[52,0],[62,3],[72,5],[82,2],[92,-4]],
        spr: [[0,-14],[4,2],[9,14],[14,18],[18,12],[22,-30],[27,-26],[34,-12],[44,0],[56,5],[68,7],[80,3],[91,-6]] },
      fro: { t: "Inversion / Eversion", sd: 3,
        kf: [[0,2],[8,-3],[20,-5],[35,-3],[50,0],[62,4],[75,5],[88,3]], run: [[0,3],[8,-5],[20,-7],[34,-4],[48,1],[60,6],[74,7],[88,4]] },
      tra: { t: "Vorfußrotation", sd: 3,
        kf: [[0,-2],[15,-4],[35,-2],[55,2],[70,5],[85,2]], run: [[0,-3],[15,-6],[35,-3],[55,3],[70,7],[85,3]] }
    }
  },
  lumbar: {
    label: "Lendenwirbelsäule", order: 4.5, kinetic: false,
    planes: {
      sag: { t: "Flexion / Extension", sd: 1.5, kf: [[0,1.5],[25,0],[50,1.5],[75,0]], car: [[0,-3],[25,-4],[50,-3],[75,-4]],
             run: [[0,4],[14,0.5],[25,1.5],[38,4.5],[50,4],[64,0.5],[75,1.5],[88,4.5]] },
      fro: { t: "Lateralflexion", sd: 2, kf: [[0,1],[14,4],[28,3],[42,0],[50,-1],[64,-4],[78,-3],[92,0]],
             car: [[0,0.4],[14,1.6],[28,1.2],[42,0],[50,-0.4],[64,-1.6],[78,-1.2],[92,0]],
             run: [[0,2],[12,7],[26,6],[40,1],[50,-2],[62,-7],[76,-6],[90,-1]] },
      tra: { t: "Rotation", sd: 2.5, kf: [[0,-6],[15,-2.4],[30,1.2],[45,4.8],[50,6],[62,3.6],[78,-1.2],[90,-4.8]],
             car: [[0,-2],[15,-0.8],[30,0.4],[45,1.6],[50,2],[62,1.2],[78,-0.4],[90,-1.6]],
             run: [[0,-11],[15,-4],[30,3],[45,9],[55,11],[70,4],[85,-6]] }
    }
  },
  thorax: {
    label: "Brustwirbelsäule", order: 4.6, kinetic: false,
    planes: {
      sag: { t: "Flexion / Extension", sd: 1, kf: [[0,-1],[25,0],[50,-1],[75,0]], car: [[0,2],[25,1.5],[50,2],[75,1.5]],
             run: [[0,-1.6],[14,-2.6],[25,-2.2],[38,-1.2],[50,-1.6],[64,-2.6],[75,-2.2],[88,-1.2]] },
      fro: { t: "Lateralflexion", sd: 1.5, kf: [[0,0.5],[14,2],[28,1.5],[42,0],[50,-0.5],[64,-2],[78,-1.5],[92,0]],
             car: [[0,0.2],[14,0.8],[28,0.6],[42,0],[50,-0.2],[64,-0.8],[78,-0.6],[92,0]],
             run: [[0,1.5],[12,6],[26,5.5],[40,1],[50,-1.5],[62,-6],[76,-5.5],[90,-1]] },
      tra: { t: "Rotation", sd: 2, kf: [[0,-3],[15,-1.2],[30,0.6],[45,2.4],[50,3],[62,1.8],[78,-0.6],[90,-2.4]],
             car: [[0,-1],[15,-0.4],[30,0.2],[45,0.8],[50,1],[62,0.6],[78,-0.2],[90,-0.8]],
             run: [[0,-7],[15,-2.5],[30,2],[45,6],[55,7],[70,2.5],[85,-4]] }
    }
  },
  shoulder: {
    label: "Schulter", order: 5, sided: true, kinetic: false,
    planes: {
      sag: { t: "Flexion / Extension", sd: 6,
        kf: [[0,-18],[12,-14],[25,-8],[38,0],[50,8],[60,14],[70,16],[80,10],[90,-4],[96,-14]],
        car: [[0,12],[25,12.6],[50,12],[75,12.6]], run: [[2,-38],[14,-30],[26,-15],[38,3],[50,19],[60,27],[68,28],[78,17],[88,-4],[96,-29]],
        spr: [[2,-54],[14,-43],[26,-20],[38,10],[50,39],[60,56],[68,58],[78,35],[88,-5],[96,-42]] },
      fro: { t: "Adduktion / Abduktion", sd: 3,
        kf: [[0,-9],[20,-7],[40,-5],[55,-4],[70,-6],[88,-8]], car: [[0,-37],[25,-37],[50,-37],[75,-37]], run: [[0,-15],[20,-13],[40,-10],[55,-9],[70,-12],[88,-14]] },
      tra: { t: "Innen- / Außenrotation", sd: 3,
        kf: [[0,4],[25,2],[50,-2],[70,-4],[90,2]], car: [[0,0],[25,0],[50,0],[75,0]], run: [[0,-6],[25,-10],[50,-15],[70,-13],[90,-8]] }
    }
  },
  mtp: {
    label: "Zehengrundgelenk", order: 4, sided: true, kinetic: false,
    planes: { sag: { t: "Extension / Flexion", sd: 6,
      kf: [[0,0],[8,-2],[20,2],[32,7],[44,17],[52,32],[58,48],[62,55],[66,40],[72,15],[79,3],[88,0]],
      run: [[0,-3],[6,1],[12,4],[18,9],[22,17],[26,33],[29,50],[31,55],[35,38],[40,20],[47,7],[57,1],[72,-1],[88,-3]],
      spr: [[0,-4],[5,0],[10,4],[14,11],[17,24],[20,46],[22,56],[26,40],[32,20],[40,6],[55,0],[75,-2],[90,-4]] } }
  },
  elbow: {
    label: "Ellbogen", order: 6, sided: true, kinetic: false,
    planes: { sag: { t: "Flexion", sd: 7, kf: [[0,22],[15,26],[30,32],[45,40],[55,44],[65,42],[78,34],[90,26]], car: [[0,53],[25,54],[50,53],[75,54]], run: [[2,60],[14,64],[26,72],[38,84],[50,95],[60,100],[68,98],[78,89],[88,76],[96,64]],
      spr: [[2,56],[14,62],[26,76],[38,96],[50,120],[60,130],[68,128],[78,110],[88,84],[96,62]] } }
  }
}

export type JointKey = keyof typeof JOINTS
